/* eslint-disable no-await-in-loop */
import React from "react";
import { useTranslation } from "react-i18next";

import { showErrorToast } from "@shared/toasts/Toasts";

import CircularTextBuffer from "./CircularTextBuffer";

type StreamStatus = "initializing" | "streaming" | "connected" | "done" | null;

type Props = {
    onStartStreaming: () => Promise<Response>;
    onStatusChange: (status: StreamStatus) => void;
};

/**
 * Hook to stream logs efficiently into a DOM container.
 *
 * No React state used for streaming data.
 * All heavy work is done outside reconciliation.
 */
export function useStreamingLog(props: Props) {
    const containerRef = React.useRef<HTMLPreElement | null>(null);
    const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
    const cancelledRef = React.useRef(false);
    const readerRef = React.useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
    const rafIdRef = React.useRef<number | null>(null);
    const bufferRef = React.useRef(new CircularTextBuffer());
    // Incomplete line carried over from previous chunk (no trailing \n).
    const lineBufferRef = React.useRef("");
    /**
     * Track how many blocks have already been flushed.
     * We do not reconstruct entire text — we append incrementally.
     */
    const renderedBlockCountRef = React.useRef(0);

    const { t } = useTranslation(["common"]);

    /**
     * Renders a batch of blocks from buffer to DOM.
     * Limits blocks per frame to avoid blocking main thread;
     * uses DocumentFragment for single appendChild.
     */
    function onRenderBlocksBatch(htmlElement: HTMLPreElement) {
        const maxBlocksPerFrame = 75;
        const fragment = document.createDocumentFragment();
        const blocks = bufferRef.current.forEachFromWithLimit(renderedBlockCountRef.current, maxBlocksPerFrame, (block) => {
            fragment.appendChild(document.createTextNode(block));
        });
        if (fragment.hasChildNodes()) {
            htmlElement.appendChild(fragment);
        }
        renderedBlockCountRef.current += blocks;
    }

    // Removes oldest nodes from the top to match buffer size.
    function onRemoveOldestNodes(htmlElement: HTMLPreElement) {
        const targetSize = bufferRef.current.getSize();
        const excessCount = htmlElement.childNodes.length - targetSize;
        if (excessCount > 0) {
            const nodesToKeep = Array.from(htmlElement.childNodes).slice(excessCount);
            htmlElement.replaceChildren(...nodesToKeep);
        }
    }

    // Flushes the buffer to the DOM.
    function onFlush() {
        if (cancelledRef.current || !containerRef.current) {
            rafIdRef.current = null;
            return;
        }

        onRenderBlocksBatch(containerRef.current);

        onRemoveOldestNodes(containerRef.current);

        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }

        rafIdRef.current = null;

        if (renderedBlockCountRef.current < bufferRef.current.getSize()) {
            rafIdRef.current = requestAnimationFrame(onFlush);
        }
    }

    function onWaitForNextFrame() {
        return new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }

    /**
     * Pauses when render backlog exceeds threshold.
     * Yields to main thread so onFlush can run, then resumes.
     */
    async function onBackpressure() {
        const pendingBlocksThreshold = 150;
        while (bufferRef.current.getSize() - renderedBlockCountRef.current > pendingBlocksThreshold && !cancelledRef.current) {
            await onWaitForNextFrame();
            if (rafIdRef.current === null) {
                rafIdRef.current = requestAnimationFrame(onFlush);
            }
        }
    }

    /**
     * Splits text by newlines and pushes each line to the circular buffer.
     * Keeps the last (possibly incomplete) line in lineBufferRef for the next chunk.
     * When the buffer drops old blocks due to capacity, adjusts renderedBlockCountRef
     * so the incremental render index stays in sync.
     */
    function pushChunkLinesToBuffer(text: string) {
        const lines = text.split("\n");
        lineBufferRef.current = lines.pop() ?? "";

        let totalDropped = 0;

        for (const line of lines) {
            totalDropped += bufferRef.current.push(line + "\n");
        }

        if (totalDropped > 0) {
            renderedBlockCountRef.current = Math.max(0, renderedBlockCountRef.current - totalDropped);
        }
    }

    async function onRunStream() {
        bufferRef.current.clear();
        lineBufferRef.current = "";
        cancelledRef.current = false;
        rafIdRef.current = null;
        renderedBlockCountRef.current = 0;

        try {
            props.onStatusChange("initializing");

            const response = await props.onStartStreaming();
            props.onStatusChange("connected");
            if (!response.body) {
                throw new Error(t("common:toast.noStreamingBodyAvailable"));
            }

            const decoder = new TextDecoder();
            readerRef.current = response.body.getReader();

            let firstChunk = true;

            while (!cancelledRef.current) {
                const { done, value } = await readerRef.current.read();
                if (done) {
                    props.onStatusChange("done");
                    if (lineBufferRef.current) {
                        bufferRef.current.push(lineBufferRef.current);
                        lineBufferRef.current = "";
                    }
                    break;
                }

                const decoded = decoder.decode(value, { stream: true });
                const text = lineBufferRef.current + decoded;

                pushChunkLinesToBuffer(text);

                if (firstChunk) {
                    props.onStatusChange("streaming");
                    firstChunk = false;
                }

                if (rafIdRef.current === null) {
                    rafIdRef.current = requestAnimationFrame(onFlush);
                }

                await onBackpressure();
            }

            // Final onFlush
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
            onFlush();
        } catch (error) {
            showErrorToast((error as Error).message);
            props.onStatusChange(null);
        }
    }

    function onClearStream() {
        props.onStatusChange(null);
        bufferRef.current.clear();
        lineBufferRef.current = "";
        cancelledRef.current = true;
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
        }
        if (readerRef.current) {
            readerRef.current.cancel();
        }
    }

    return {
        onRunStream,
        onClearStream,
        containerRef,
        scrollContainerRef,
    };
}
